import {
  BoardBuilderGames,
  BoardBuilderProjections,
  BoardBuilderProjectionsFilters
} from '@bo-components/boardBuilder';
import type {
  LeagueInfo,
  LeagueProjectionAttributes
} from '@library/types/leagues';
import { useMemo, useState } from 'react';
import type { AddDelayType } from '@library/types/boardBuilder';
import { BoardBuilderContext } from '@bo-hooks/boardBuilder/useBoardBuilderContext';
import type { LeagueGames } from '@library/types/games';
import { getMainDuration } from '@bo-utils/leagueAttributes';
import { useBoardBuilder } from '@bo-hooks/boardBuilder/useBoardBuilder';
import { useBoardBuilderFilters } from '@bo-hooks/boardBuilder/useBoardBuilderFilters';

export interface BoardBuilderProps {
  bulkLeagueIdGroups: Array<Array<string>>;
  durations: Array<LeagueProjectionAttributes>;
  games?: LeagueGames;
  isLeagueGamesError: boolean;
  isLeagueGamesLoading: boolean;
  leagueInfo: LeagueInfo;
  searchValue?: string;
  selectedDate: string;
  selectedDuration: string;
  selectedGames: Array<string>;
  selectedPositions: Array<string>;
  selectedTeams: Array<string>;
  onResetSearch: () => void;
}

export const BoardBuilder = ({
  bulkLeagueIdGroups,
  durations,
  games = [],
  isLeagueGamesError,
  isLeagueGamesLoading,
  leagueInfo,
  searchValue = '',
  selectedDate,
  selectedDuration,
  selectedGames,
  selectedPositions,
  selectedTeams,
  onResetSearch
}: BoardBuilderProps) => {
  const [gameStartTimeDelay, setGameStartTimeDelay] = useState<AddDelayType>({
    gameId: '',
    delay: 0,
    isTimePickerChange: false
  });

  const handleAddDelay = ({
    gameId,
    delay,
    isTimePickerChange
  }: AddDelayType) => {
    setGameStartTimeDelay({ gameId, delay, isTimePickerChange });
  };

  const { boardBuilderContext } = useBoardBuilder({
    bulkLeagueIdGroups,
    durations,
    games,
    leagueInfo,
    searchValue,
    selectedDate,
    selectedDuration,
    selectedGames,
    selectedPositions,
    selectedTeams
  });

  const {
    confirmedGameModes,
    confirmedPlayersList,
    confirmedStatTypes,
    gameModes,
    handleDurationChange,
    handleCancelSelectChanges,
    handleConfirmSelectChanges,
    handleGameModesChange,
    handlePopulateRankInfo,
    handleResetFilters,
    handleResetRankInfo,
    hasSelectionChanged,
    hasSelections,
    handleSelectPlayer,
    handleSelectStatTypes,
    handleUnSelectPlayer,
    newlySelectedGameModes,
    newlySelectedPlayersList,
    newlySelectedStatTypes,
    rankInfo,
    selectedDurationId,
    selectedGameModes,
    selectedPlayersList,
    selectedStatTypes,
    selectedLeagueDurationAttributes,
    statTypes,
    unSelectedGameModes,
    unSelectedPlayersList,
    unSelectedStatTypes
  } = useBoardBuilderFilters({
    initialDurationId: selectedDuration,
    leagueInfo
  });

  const mainDurationId = useMemo(
    () =>
      getMainDuration({
        durations,
        leagueName: leagueInfo.name
      })?.leagueProjectionFilterId,
    [durations, leagueInfo]
  );

  const gameStartTimes = useMemo(() => {
    const gameStartTimeData: Record<string, string> = {};
    games.forEach((game) => {
      gameStartTimeData[game.game_id] = game.date_time;
    });
    return gameStartTimeData;
  }, [games]);

  return (
    <BoardBuilderContext.Provider value={boardBuilderContext}>
      <div className="flex size-full pb-2">
        <BoardBuilderGames
          games={games}
          onAddDelay={handleAddDelay}
          onSelectPlayer={handleSelectPlayer}
          onUnSelectPlayer={handleUnSelectPlayer}
          isLeagueGamesError={isLeagueGamesError}
          isLeagueGamesLoading={isLeagueGamesLoading}
          leagueInfo={leagueInfo}
          searchValue={searchValue}
          selectedDate={selectedDate}
          selectedGames={selectedGames}
          selectedPlayersList={selectedPlayersList}
          selectedPositions={selectedPositions}
          selectedTeams={selectedTeams}
        />
        <section className="bg-soFresh-110 flex w-full flex-col p-4">
          <BoardBuilderProjectionsFilters
            durations={durations}
            gameModes={gameModes}
            hasSelectionChanged={hasSelectionChanged}
            mainDurationId={mainDurationId ?? ''}
            newlySelectedGameModes={newlySelectedGameModes}
            newlySelectedPlayersList={newlySelectedPlayersList}
            newlySelectedStatTypes={newlySelectedStatTypes}
            onCancelSelectChanges={handleCancelSelectChanges}
            onConfirmSelectChanges={handleConfirmSelectChanges}
            onDurationChange={handleDurationChange}
            onGameModesChange={handleGameModesChange}
            onRanking={handlePopulateRankInfo}
            onStatTypesChange={handleSelectStatTypes}
            rankDisabled={false}
            selectedDurationId={selectedDurationId}
            selectedGameModes={selectedGameModes}
            selectedStatTypes={selectedStatTypes}
            statTypes={statTypes}
            unSelectedGameModes={unSelectedGameModes}
            unSelectedPlayersList={unSelectedPlayersList}
            unSelectedStatTypes={unSelectedStatTypes}
          />
          <BoardBuilderProjections
            confirmedGameModes={confirmedGameModes}
            confirmedPlayersList={confirmedPlayersList}
            confirmedStatTypes={confirmedStatTypes}
            durations={durations}
            gameStartTimeDelay={gameStartTimeDelay}
            gameStartTimes={gameStartTimes}
            hasSelections={hasSelections}
            leagueInfo={leagueInfo}
            mainDurationId={mainDurationId ?? ''}
            onAddDelay={handleAddDelay}
            onResetRankInfo={handleResetRankInfo}
            onResetFilters={handleResetFilters}
            onResetSearch={onResetSearch}
            rankInfo={rankInfo}
            selectedDurationId={selectedDurationId}
            selectedLeagueDurationAttributes={selectedLeagueDurationAttributes}
            statTypes={statTypes}
          />
        </section>
      </div>
    </BoardBuilderContext.Provider>
  );
};
